import React from "react";
import { motion } from "framer-motion";

const SILHOUETTE = "#E5E7EB";
const MAROON = "#5C0E2E";
const MAROON_LIGHT = "#7A1640";
const ORANGE = "#EE6C2E";
const ORANGE_LIGHT = "#F2913A";

const BuildingIllustration = ({ className = "" }) => (
  <svg
    viewBox="0 0 640 500"
    fill="none"
    className={className}
    role="img"
    aria-label="Illustration of people building a house made of code blocks"
  >
    <ellipse cx="330" cy="462" rx="230" ry="16" fill="#000" opacity="0.25" />
    <line
      x1="60" y1="452" x2="600" y2="452"
      stroke="#374151" strokeWidth="2" strokeDasharray="7 7" strokeLinecap="round"
    />

    {/* ladder */}
    <line x1="418" y1="352" x2="440" y2="238" stroke="#4B5563" strokeWidth="5" strokeLinecap="round" />
    <line x1="440" y1="352" x2="462" y2="238" stroke="#4B5563" strokeWidth="5" strokeLinecap="round" />
    {[260, 288, 316, 344].map((y) => (
      <line
        key={y}
        x1={420 + (352 - y) * 0.12} y1={y}
        x2={442 + (352 - y) * 0.12} y2={y}
        stroke="#4B5563" strokeWidth="4" strokeLinecap="round"
      />
    ))}

    {/* base block */}
    <rect x="240" y="340" width="190" height="112" rx="12" fill={MAROON} stroke={MAROON_LIGHT} strokeWidth="2" />
    <rect x="266" y="372" width="34" height="34" rx="6" fill={ORANGE_LIGHT} opacity="0.18" stroke={ORANGE} strokeWidth="1.5" />
    <rect x="396" y="372" width="34" height="34" rx="6" fill={ORANGE_LIGHT} opacity="0.18" stroke={ORANGE} strokeWidth="1.5" />

    {/* middle block */}
    <rect x="278" y="238" width="124" height="102" rx="10" fill={ORANGE} stroke={ORANGE_LIGHT} strokeWidth="2" />
    <text x="340" y="298" fontFamily="monospace" fontSize="30" fontWeight="700" fill="#ffffff" opacity="0.9" textAnchor="middle">
      {"</>"}
    </text>

    {/* roof under construction */}
    <path
      d="M270,238 L340,166 L410,238"
      stroke="#6B7280" strokeWidth="3" strokeDasharray="9 7" strokeLinecap="round" fill="none"
    />
    <g transform="translate(302,200) rotate(-26)">
      <rect x="-36" y="-13" width="72" height="26" rx="5" fill={MAROON} stroke={MAROON_LIGHT} strokeWidth="2" />
    </g>

    {/* gear, slow spin */}
    <motion.g
      style={{ originX: "556px", originY: "128px" }}
      animate={{ rotate: 360 }}
      transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      opacity="0.35"
    >
      <circle cx="556" cy="128" r="22" fill="none" stroke="#6B7280" strokeWidth="5" />
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * Math.PI) / 4;
        const x1 = 556 + Math.cos(angle) * 22;
        const y1 = 128 + Math.sin(angle) * 22;
        const x2 = 556 + Math.cos(angle) * 30;
        const y2 = 128 + Math.sin(angle) * 30;
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6B7280" strokeWidth="5" strokeLinecap="round" />
        );
      })}
    </motion.g>

    {/* floating particles */}
    <circle cx="120" cy="140" r="4" fill={ORANGE} opacity="0.5" />
    <circle cx="500" cy="90" r="3" fill={ORANGE_LIGHT} opacity="0.5" />
    <circle cx="150" cy="230" r="3" fill={MAROON_LIGHT} opacity="0.5" />

    {/* Person A: placing the roof panel, standing on the middle block */}
    <g>
      <line x1="328" y1="238" x2="328" y2="206" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" />
      <line x1="350" y1="238" x2="350" y2="206" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" />
      <rect x="320" y="172" width="38" height="46" rx="15" fill={SILHOUETTE} />
      <path d="M316,190 L292,208" stroke={SILHOUETTE} strokeWidth="9" strokeLinecap="round" />
      <path d="M362,190 L348,205" stroke={SILHOUETTE} strokeWidth="9" strokeLinecap="round" />
      <circle cx="339" cy="160" r="15" fill={SILHOUETTE} />
      <path d="M324,158 a15,15 0 0 1 30,0 Z" fill={ORANGE} />
      <rect x="321" y="157" width="36" height="5" rx="2.5" fill={ORANGE} />
    </g>

    {/* Person B: seated with laptop, coding at the base */}
    <g>
      <path d="M150,430 Q142,400 158,392" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" fill="none" />
      <path d="M176,430 Q184,404 196,398" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" fill="none" />
      <rect x="148" y="360" width="36" height="44" rx="15" fill={SILHOUETTE} />
      <path d="M150,378 L128,392" stroke={SILHOUETTE} strokeWidth="8" strokeLinecap="round" />
      <path d="M182,378 L200,396" stroke={SILHOUETTE} strokeWidth="8" strokeLinecap="round" />
      <circle cx="166" cy="347" r="13" fill={SILHOUETTE} />
      <path d="M153,345 a13,13 0 0 1 26,0 Z" fill={ORANGE} />
      <rect x="151" y="344" width="30" height="4.5" rx="2.25" fill={ORANGE} />

      {/* laptop */}
      <rect x="190" y="402" width="48" height="6" rx="2" fill="#6B7280" />
      <polygon points="196,402 234,402 226,362 204,362" fill="#1F2937" stroke={ORANGE} strokeWidth="1.5" />
      <rect x="207" y="372" width="14" height="2.4" fill={ORANGE} opacity="0.7" />
      <rect x="206" y="379" width="18" height="2.4" fill={ORANGE} opacity="0.5" />
      <rect x="207" y="386" width="10" height="2.4" fill={ORANGE} opacity="0.7" />
      <motion.rect
        x="219" y="386" width="2.4" height="2.4" fill={ORANGE_LIGHT}
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 1.1, repeat: Infinity }}
      />
    </g>

    {/* Person C: carrying a block toward the build */}
    <g>
      <line x1="486" y1="378" x2="480" y2="430" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" />
      <line x1="506" y1="378" x2="514" y2="430" stroke={SILHOUETTE} strokeWidth="11" strokeLinecap="round" />
      <rect x="480" y="332" width="36" height="46" rx="15" fill={SILHOUETTE} />
      <path d="M482,344 Q462,352 458,332" stroke={SILHOUETTE} strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M514,344 Q534,352 538,332" stroke={SILHOUETTE} strokeWidth="8" strokeLinecap="round" fill="none" />
      <circle cx="498" cy="319" r="13" fill={SILHOUETTE} />
      <path d="M485,317 a13,13 0 0 1 26,0 Z" fill={ORANGE} />
      <rect x="483" y="316" width="30" height="4.5" rx="2.25" fill={ORANGE} />

      {/* carried block */}
      <rect x="452" y="300" width="66" height="42" rx="7" fill={MAROON_LIGHT} stroke={MAROON} strokeWidth="2" />
      <circle cx="459" cy="307" r="2.6" fill={ORANGE} />
      <circle cx="511" cy="307" r="2.6" fill={ORANGE} />
      <circle cx="459" cy="335" r="2.6" fill={ORANGE} />
      <circle cx="511" cy="335" r="2.6" fill={ORANGE} />
    </g>
  </svg>
);

export default BuildingIllustration;
